import { useVitalsQueryWithFilters } from "@tellescope/tanstack-query";
import { VitalsSectionItem } from "./VitalsSectionItem";
import { Button, DateRangeField, MultiSelect } from "@tellescope/ui";
import { Spinner, Select } from "@tellescope/design-system";
import { ObservationType } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { TimezoneField, useForm } from "@tellescope/forms";
import { useCallback, useMemo } from "react";
import { getProgramForOptionValue, useVitalProgramsAndThresholds } from "../../../hooks";
import { BlockPaginatedList } from "../BlockPaginatedList";
import { BlockEmptyState } from "../BlockEmptyState";

const MS_IN_DAY = 1000 * 60 * 60 * 24;

export interface VitalsRecordsListProps {
  enduserId: string;
  onItemClick: (observationId: string, observationType: ObservationType, title: string) => void;
  onAddReading: (type: "bp" | "other") => void;
  hideButtons?: boolean;
  /**
   * Renders the date-range / program / threshold / timezone filter row above the list. Off for the
   * collapsed in-page block, which shows rows only; on for the expanded and sidebar views.
   */
  showFilters?: boolean;
  canUpdateObservations: boolean;
}

export const VitalsRecordsList = ({
  enduserId,
  onItemClick,
  onAddReading,
  hideButtons,
  showFilters = true,
  canUpdateObservations,
}: VitalsRecordsListProps) => {
  const { t } = useTranslation("vitals");

  const { vitalPrograms, programOptions, thresholdOptions, defaultProgramConfigIds } = useVitalProgramsAndThresholds();

  const getConfigIdsForProgram = useCallback(
    (programId: string) => {
      if (programId === "all") return [];
      return getProgramForOptionValue(vitalPrograms?.programs, programId)?.configurationIds ?? [];
    },
    [vitalPrograms?.programs]
  );

  const filterForm = useForm({
    fields: {
      dateRange: {
        type: "dateRange",
        initialValue: {},
      },
      programId: {
        type: "select",
        initialValue: "all",
      },
      thresholdIds: {
        type: "multiselect",
        initialValue: [],
      },
      timezone: {
        type: "timezone",
        initialValue: undefined,
      },
    },
    sideEffects: {
      programId: (newProgramId) => ({
        thresholdIds: getConfigIdsForProgram(newProgramId) ?? [],
      }),
    },
  });

  const convertedDateRange = useMemo<
    NonNullable<NonNullable<Parameters<typeof useVitalsQueryWithFilters>[0]>["filters"]>["dateRange"]
  >(() => {
    if (filterForm.values.dateRange.from === undefined || filterForm.values.dateRange.to === undefined) {
      return undefined;
    }

    return {
      from: new Date(filterForm.values.dateRange.from),
      to: new Date(filterForm.values.dateRange.to + MS_IN_DAY),
    };
  }, [filterForm.values.dateRange.from, filterForm.values.dateRange.to]);

  const configurationIds = filterForm.values.thresholdIds;

  const effectiveThresholdIds = useMemo(
    () => (configurationIds?.length ? configurationIds : defaultProgramConfigIds),
    [configurationIds, defaultProgramConfigIds]
  );

  const observationsQuery = useVitalsQueryWithFilters({
    limit: 50,
    filters: {
      enduserIds: [enduserId],
      dateRange: convertedDateRange,
      configurationIds: configurationIds?.length ? configurationIds : undefined,
      validStatus: "valid",
    },
    combineRelatedObservations: true,
  });

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {showFilters && (
        <div className="flex flex-wrap gap-2 pb-4 items-center">
          <DateRangeField
            placeholder={t("Pick a date range")}
            {...filterForm.getCustomFieldProps("dateRange")}
            className="text-xs text-foreground"
          />
          <Select {...filterForm.getCustomFieldProps("programId")}>
            <Select.Trigger className="text-xs text-muted-foreground hover:bg-muted">
              <Select.Value placeholder={t("All Programs")} />
            </Select.Trigger>
            <Select.Content>
              <Select.Item value="all">{t("All Programs")}</Select.Item>
              {programOptions.map((program) => (
                <Select.Item key={program.value} value={program.value}>
                  {program.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>
          <MultiSelect
            {...filterForm.getCustomFieldProps("thresholdIds")}
            options={thresholdOptions}
            placeholder={t("Select thresholds")}
            searchPlaceholder={t("Search thresholds...")}
            showAsBadges={false}
            className="w-[175px] min-h-0 h-[32px] py-1.5 text-muted-foreground text-xs rounded-[10px] font-normal"
          />
          <TimezoneField
            id="vitals-timezone"
            {...filterForm.getFieldProps("timezone")}
            onChange={(newVal) => filterForm.setFieldValue("timezone", newVal)}
            className="w-[170px] min-h-6 h-[32px] py-1 text-muted-foreground text-xs hover:text-muted-foreground"
          />
          {!hideButtons && (
            <>
              <div className="flex-grow" />
              <Button variant="secondary" className="text-xs font-normal" onClick={() => onAddReading("bp")}>
                {t("Add BP Reading")}
              </Button>
              <Button variant="secondary" className="text-xs font-normal" onClick={() => onAddReading("other")}>
                {t("Add Other Reading")}
              </Button>
            </>
          )}
        </div>
      )}
      {observationsQuery.isLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Spinner />
        </div>
      ) : !observationsQuery.data?.length ? (
        <div className="flex flex-1 items-center justify-center">
          <BlockEmptyState>{t("No readings found")}</BlockEmptyState>
        </div>
      ) : (
        <BlockPaginatedList
          className="w-full pb-2"
          scrollbar="floating"
          fadeWhenClipped
          onBottomReached={() => observationsQuery.fetchNextPage()}
        >
          {observationsQuery.data?.map((observation) => (
            <VitalsSectionItem
              key={observation.id}
              observation={observation}
              className="w-full mb-2"
              onClick={onItemClick}
              timezone={filterForm.values.timezone || undefined}
              selectedThresholdIds={effectiveThresholdIds}
              canUpdateObservations={canUpdateObservations}
            />
          ))}
        </BlockPaginatedList>
      )}
    </div>
  );
};
